import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useUser, useSession } from "@clerk/clerk-react";
import { useSupabase } from "../supabaseClient";
import {
  MessageSquare, BookOpen, Lightbulb, Star, User, Send,
  Menu, X, CheckCircle, ChevronRight, Pencil, AlertTriangle, ArrowRight, Trash2,
} from "lucide-react";
import {
  MODULE_META, MODULE_PROMPTS, MODULE_TRANSITIONS,
  MOMENTUM_MODULES, ACCELERATOR_MODULES, IDEATION_MODULES, IDEATION_CONTEXT_RECEIVERS, IDEATION_CONTEXT_EXPORTERS,
} from "../constants/modules";
import { usePanelMode } from "../hooks/usePanelMode";
import { ARCHETYPE_MAP } from "../constants/archetypes";
import { flagsToCoach } from "../constants/nicheSensitivity";
import {
  parseOutputs, cleanMessageContent, extractLockedSections, resolveLocks, lockFailureAdvice,
  sectionLockState, lockedSections, LOCK_STATE, readLockReply, LOCK_REPLY, sectionProgress,
  detectCompletedPrompts, getVisiblePrompts, withNicheBoundary, belongsElsewhere, messageStamps,
} from "../utils/messages";
import { renderMarkdown } from "../utils/markdown";
import { moduleFromText } from "../utils/moduleIntent";
import { buildMomentumContext as buildSharedMomentumContext } from "../utils/momentumContext";
import { offerLadderBlock } from "../utils/offerLine";
import { parseLibraryProposal } from "../utils/coachLibrary";
import { brillToolsBlock, toolOffer } from "../utils/brillTools";
import { LibraryProposal } from "./LibraryProposal";
import { buildAcceleratorContext } from "../utils/acceleratorContext";
import { ReviewWidget } from "./ReviewWidget";
import { PanelModeToggle } from "./PanelModeToggle";
import { LibraryButton, LibraryOverlay } from "./LibraryOverlay";
import { SaveThought } from "./SaveThought";
import { AskBrillHome, useTypedExamples, AskBrillFigure, BrillMark } from "./AskBrillHome";
import { EARLIER, EARLIER_TITLE, MODES, modeOfTab, tabOfMode } from "../utils/askBrillChats";
import { parseTestimonialBlock, testimonialRow, submissionOf } from "../utils/testimonials";

/**
 * Brill's avatar. The gradient is `--brand-*` rather than a canvas token because
 * it is identity: Brill looks like Brill in both panel modes. The glyph inside is
 * literal white for the same reason — it is not canvas text.
 */
/* Brill's face in the chat is her LOGO, the four-leaf mark, white on the teal
   circle (her ask, 2026-10-22) — it was a generic robot icon. Decorative:
   the bubble beside it already says who is speaking. */
function BrillAvatarMark() {
  return <img src="/brand/brill-mark-white.svg" alt="" aria-hidden="true" width={17} height={17} style={{ display: "block" }} />;
}

const BOT_AVATAR = {
  width: 28, height: 28, borderRadius: "50%",
  background: "linear-gradient(135deg, var(--brand-teal), var(--brand-teal-light))",
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};

/* The two "start the next section" CTAs share this. */
const START_BTN = {
  background: "linear-gradient(135deg, var(--btn-grad-from), var(--btn-grad-to))",
  color: "var(--btn-text)", border: "none", borderRadius: 8,
  padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
};

/* The amber "something didn't save" strip, used for two different failures. */
const NOTICE = {
  display: "flex", alignItems: "center", gap: 6,
  background: "var(--warn-bg)", border: "1px solid var(--warn-line)",
  borderRadius: 8, fontSize: 12, color: "var(--warn-text)",
};

/* THE CHAT COLUMN — one width for the strip, the tabs, the messages and the
   box, so they cannot drift. 880px since her read on 2026-10-21 ("a little too
   small of a container space"); it was 760, with Brill's bubbles capped at 70%
   — about 517px of text on a desktop. */
const COLUMN = 880;
const COLUMN_GUTTER = `max(18px, calc((100% - ${COLUMN}px) / 2))`;
const BUBBLE_MAX = "min(75%, 680px)";

export function ChatPanel({ moduleId, messages, setMessages, loading, setLoading, onBlueprintReady, onViewBlueprint, onViewMasterBlueprint, onViewIdeationBlueprint, onSwitchModule, isMobile, onMenuOpen, rightPanel, onClosePanel, onViewOutput, savedOutputs, onOutputLocked, onReviseSection, userId, testimonialSubmitted, onTestimonialSubmitted, historyLoadFailed, onRetryHistory, userSettings, promptTrigger, onPromptTriggered, acceleratorOutputs, ideationOutputs, onStartPrompt, reviseFocusTrigger, activeNiche, outputStamps, rebuildNext, onRebuildNext, onDismissRebuildNext, brandAsk = null, onBrandAnswer, outputsLoaded = false,
  askChat = null, foundationOutputs = null, coachOffers = null, brillTools = null,
  lockFailure = null, onRetryLock, onDismissLockFailure,
  reviseTarget = null, reviseOpen = null, onReviseHandled }) {
  const [input, setInput] = useState("");
  const [transitioning, setTransitioning] = useState(null); // null or target moduleId
  /* A lock Brill proposed in the same breath as a question. Held here rather
     than committed, so she gets the choice it was only pretending to offer. */
  const [pendingLock, setPendingLock] = useState(null);
  /* She said something about locking that was not clearly a yes. The card
     says so rather than the message vanishing into nothing. */
  const [lockNearMiss, setLockNearMiss] = useState(false);
  /* A module she asked for in words. Holds a module key; the card below offers
     to take her, and nothing moves until she says so. */
  const [moduleOffer, setModuleOffer] = useState(null);

  /* Sections already asked to re-emit their output block, so a repair can never
     become a loop. Per mount, which is per module — a genuinely stuck section
     gets one silent retry and then stays visibly unlocked, which is honest. */
  const repairedRef = useRef(new Set());
  /* Sections waiting on a silent re-emit, fired by the effect below once the
     reply that caused them has finished landing. */
  const [pendingRepair, setPendingRepair] = useState(null);
  const sendRef = useRef(null);
  const [saveWarning, setSaveWarning] = useState(false);
  const [textareaFocused, setTextareaFocused] = useState(false);
  /* A saved chat reopens as the kind it was started as (0043). */
  const [chatTab, setChatTab] = useState(() => tabOfMode(askChat?.mode));
  const [faqMessages, setFaqMessages] = useState([]);
  const [mindsetMessages, setMindsetMessages] = useState([]);
  const [reviewMessages, setReviewMessages] = useState([]);
  const [reviewSubmissions, setReviewSubmissions] = useState([]);
  const [reviewSubsLoaded, setReviewSubsLoaded] = useState(false);
  const [reviewQuickStars, setReviewQuickStars] = useState(0);
  const [reviewQuickHover, setReviewQuickHover] = useState(0);
  const [reviewQuickText, setReviewQuickText] = useState("");
  const [reviewQuickSubmitting, setReviewQuickSubmitting] = useState(false);
  /* "+ Write Another" opens the whole screen again — stars, or a topic for
     Brill — rather than going straight into Brill's chat. */
  const [reviewWriting, setReviewWriting] = useState(false);
  /* One way back, the bar's own: leaving the tab is how she gets to her list. */
  useEffect(() => { if (chatTab !== "review") setReviewWriting(false); }, [chatTab]);
  /* Brill's finished testimonial, waiting for HER stars before it saves. */
  const [reviewPending, setReviewPending] = useState(null);
  const [reviewPendingStars, setReviewPendingStars] = useState(0);
  const [reviewSaveFailed, setReviewSaveFailed] = useState(false);
  const { user } = useUser();
  const { session } = useSession();

  const handleTransitionTo = (targetModule) => {
    setTransitioning(targetModule);
    setTimeout(() => onSwitchModule(targetModule), 800);
  };

  /* The coach's active niche, injected so a module can actually build from it.
     api/_lib/prompts.js tells Brill to "carry forward the niche statement from My
     Mission", but each module only ever receives its own thread — so without
     this block that instruction has nothing to act on. Small on purpose: the
     niche in a line, not the whole client portrait. */
  const buildNicheContext = () => {
    if (!activeNiche || !ACCELERATOR_MODULES.includes(moduleId)) return null;
    const lines = [`Niche: ${activeNiche.label}`];
    if (activeNiche.statement) lines.push(`In her words: ${activeNiche.statement}`);

    /* Naming the stale sections is the load-bearing half of this block.
       Without it Brill reads the transcript, sees a locked section, and obeys the
       "then move to the next one" chain in its module guide — which walked a coach
       who had just pivoted straight past two locked sections of My Market and
       dropped her into Discovery Interview Questions. It wasn't ignoring her old
       niche; nothing had told it those two sections were built on one. A locked
       stale section is the work, not evidence the work is done. */
    const stamps = outputStamps?.[moduleId] || {};
    const order = MODULE_PROMPTS[moduleId] || [];
    const stale = [...order, ...Object.keys(stamps).filter((n) => !order.includes(n))]
      .filter((name) => isStale(moduleId, name));

    if (stale.length) {
      lines.push(
        "",
        "She changed her niche partway through this module. These sections are locked, but were built for the client she no longer serves:",
        ...stale.map((name) => `- ${name}`),
        "",
        "Do not treat those as finished, and do not step over one to reach the next section. Work this module in its normal order. When you arrive at a section on that list, say plainly that it was built on her old niche, show her what is there now, and ask whether she wants it revised or left as it is — then wait for her answer before moving on.",
      );
    }

    return `[ACTIVE_NICHE — read only, do not reference as a conversation]\nThis is the niche this coach is currently building for. Everything you write in this module is for this client, not any earlier one.\n\n${lines.join("\n")}`;
  };

  /* Was this section locked under a niche the coach has since left? Only true
     when there's a definite stamp that disagrees with the active niche — an
     unstamped section is treated as current, so nothing gets wrongly discounted. */
  const isStale = (id, name) => {
    if (!activeNiche) return false;
    const stamp = outputStamps?.[id]?.[name];
    return !!stamp && stamp !== activeNiche.id;
  };

  // For Momentum modules: build a read-only context block of all Accelerator locked outputs.
  // This is injected into the API call only — never stored in state or Supabase.
  //
  // Sections built on an earlier niche are labelled rather than dropped. Dropping
  // them would leave Momentum with holes it can't see; presenting them unlabelled
  // as "the locked record" is worse — it would have Brill write new work from a
  // client the coach no longer serves.
  const buildMomentumContext = () => {
    /* An Ask Brill COACH reads its slice of her foundation (CONTEXT_SCOPES, by
       the coach's moduleKey), so it knows Vision once she has Vision and all of
       it once she has finished. A section she has not reached is simply absent. */
    const kind = moduleId === "chat" ? MODES[modeOfTab(chatTab)] : null;
    if (kind?.coach) {
      /* And her whole Offer Ladder (her call, 2026-10-22) — what she sells
         TODAY, which can be newer than the offer she locked in the Accelerator. */
      return [
        buildSharedMomentumContext({ moduleId: kind.moduleKey, acceleratorOutputs: foundationOutputs, activeNiche, isStale }),
        offerLadderBlock(coachOffers),
      ].filter(Boolean).join("\n\n") || null;
    }
    if (!MOMENTUM_MODULES.includes(moduleId)) return null;
    // Shared with the Offer Ladder's drafting chat — see utils/momentumContext.js.
    // No scope passed, so these modules keep receiving the whole locked record.
    return buildSharedMomentumContext({ moduleId, acceleratorOutputs, activeNiche, isStale });
  };

  /* ── Revising one locked section ─────────────────────────────────────────
   *
   * THE BUG (2026-08-27). Pressing Revise pushed "Here's your locked X — what
   * would you like to change?" into the transcript as an ASSISTANT message, and
   * that was the only signal Brill ever got. So when she typed her change, the
   * turn Brill received was: something it believes it wrote, then her words.
   * Nothing in it instructs a revision — it had to infer one from a preamble
   * rule while the module's own prescriptive script pulled it toward running
   * the prompts in order. The script tends to win. Same failure as the Redo
   * flow: an assistant message cannot instruct Brill.
   *
   * The obvious fix does not work: prepending the instruction to her message
   * would hide her own words, because `cleanMessageContent` strips
   * `[PROMPT_START:…]` from the start of a string TO THE END.
   *
   * So it goes where the other instructions already go — an injected user turn
   * ahead of the conversation, never stored, never displayed. One send only:
   * `onReviseHandled` clears it so the next message is an ordinary one. */
  const buildReviseContext = () => {
    if (!reviseTarget) return null;
    return `[REVISE — read only, do not reference as a conversation]
`
      + `She has asked to revise ONE locked section: "${reviseTarget}". Her next `
      + `message is the change she wants.
`
      + `Rewrite only that section, incorporating what she says, keeping the same `
      + `structure and format as the version she already has. Output it in the `
      + `standard lock block with the SAME section name so it replaces cleanly. `
      + `Acknowledge what changed in one sentence, then stop.
`
      + `Do not re-run this section's intake questions, do not start any other `
      + `section, and do not move on through the module. If the change would `
      + `cascade into other locked sections, name which ones and leave them alone.`;
  };

  // For later Ideation modules: inject locked outputs from prior ideation tools.
  // Injected into the API call only — never stored in state or Supabase.
  /* ── The lead turns, in one place ────────────────────────────────────────
   *
   * The module context and the locked-sections context are ADDITIVE, not
   * alternatives. The `||` chain below picks the one builder that belongs to
   * this kind of module — momentum, ideation or the niche — and the locked
   * sections ride alongside it, because a coach whose transcript is missing her
   * own work still needs her niche in the same turn.
   *
   * Assembled here rather than at each call site: there are two send paths, the
   * one she types into and the one a prompt trigger fires, and every context
   * added so far has had to be remembered in both. */
  const buildLeadContexts = () => [
    buildMomentumContext() || buildIdeationContext() || buildNicheContext(),
    buildAcceleratorContext({ moduleId, savedOutputs, messages: activeMessages }),
    /* Ask Brill, every kind of chat: what is in Brill and how to send her
       there (utils/brillTools.js). */
    moduleId === "chat" && chatTab !== "review" ? brillToolsBlock(brillTools) : null,
  ].filter(Boolean);

  const buildIdeationContext = () => {
    if (!IDEATION_CONTEXT_RECEIVERS.includes(moduleId) || !ideationOutputs) return null;
    const labels = {
      decoder: "Niche Decoder — Your Decoded Statement",
    };
    const sections = IDEATION_CONTEXT_EXPORTERS
      .filter(id => ideationOutputs[id] && Object.keys(ideationOutputs[id]).length > 0)
      .map(id => {
        const body = Object.values(ideationOutputs[id]).join("\n\n");
        return `--- ${labels[id]} ---\n${body}`;
      }).join("\n\n");
    if (!sections) return null;
    return `[IDEATION_CONTEXT — read only, do not reference as a conversation]\nThe following are this coach's confirmed locked outputs from the Ideation Academy. Use them as context for this session. Do not treat them as part of the chat — they are the locked record.\n\n${sections}`;
  };

  // Fire a real API call when a prompt is triggered from the sidebar
  useEffect(() => {
    if (!promptTrigger || promptTrigger.moduleId !== moduleId || loading) return;
    onPromptTriggered();
    /* Four shapes of trigger, because a niche pivot asks four different things of
       Brill and the module guide in the system prompt is prescriptive enough to
       reassert itself over any of them if the turn is vague.

         no redo        — start a section, the plain sidebar case.
         redo "stale"   — the section is locked but was built on a niche she has
                          left. Revise it, carrying forward what still holds.
         redo "current" — locked and already on her current niche. Do NOT rebuild;
                          show it and ask. Stepping over these silently is what
                          made a rebuild feel like it was jumping around, so the
                          walkthrough stops here too.
         redo "fresh"   — never locked. Build it for the current niche.

       `step` is present only inside a module rebuild the coach started from the
       niche review. It exists to stop Brill running ahead: its guide ends nearly
       every section with "then move to X", and in a rebuild the app is what opens
       the next section, after the lock has been stamped to the new niche.

       All four keep the [PROMPT_START:] prefix so cleanMessageContent hides the
       turn from the transcript. */
    const redo = promptTrigger.redo;
    const step = redo?.step;
    const head = `[PROMPT_START: ${promptTrigger.promptName}]`;
    const niches = redo
      ? [
        redo.oldNiche ? `\nShe used to serve: ${redo.oldNiche}` : "",
        redo.newNiche ? `\nShe now serves: ${redo.newNiche}` : "",
      ].join("")
      : "";
    const stepLine = step
      ? `\n\nThis is step ${step.index} of ${step.total} in a rebuild of this module that she started deliberately. Handle ONLY "${promptTrigger.promptName}". When it is settled, stop there — do not introduce, preview or begin the next section. The app opens the next one for her.`
      : "";

    let triggerContent;
    if (!redo) {
      triggerContent = `${head} The coach has selected "${promptTrigger.promptName}" from the sidebar. Begin this specific section now following your guide instructions for it. Reference any relevant context already in this conversation but do not re-run completed sections.`;
    } else if (redo.state === "current") {
      triggerContent = [
        `${head} She has changed her niche and is walking this module from the top. "${promptTrigger.promptName}" is already locked and was already built on her current niche, so it is probably fine.`,
        niches,
        `\n\nDo not rebuild it and do not re-run the section's opening questions. Show her the version that is locked right now — quoted or summarised in a few lines, not rewritten — say it was built on the niche she is on today so it should still hold, and ask whether she wants to revise it or leave it and move on. Then stop and wait for her answer.`,
        stepLine,
      ].join("");
    } else if (redo.state === "fresh") {
      triggerContent = [
        `${head} She has changed her niche and is walking this module from the top. "${promptTrigger.promptName}" has never been locked, so build it fresh for the niche she is on now.`,
        niches,
        `\n\nFollow your guide instructions for this section. Do not re-run the sections above it.`,
        stepLine,
      ].join("");
    } else {
      triggerContent = [
        step
          ? `${head} She has changed her niche and is walking this module from the top. "${promptTrigger.promptName}" is locked, but it was built on the niche she has left.`
          : `${head} The coach has changed her niche and asked you to revise this ONE section. She has not asked you to restart the module.`,
        niches,
        `\n\nDo not re-run this module's opening questions and do not build the section from scratch. The existing version is in the conversation above — work from it.`,
        `\n\nRespond in this order:`,
        `\n1. In two or three short bullets, name what in the existing version still holds under the new niche, and why. Look for the method, the structure, the emotional pattern, her voice — anything about how she works rather than who she serves.`,
        `\n2. Name plainly what was specific to the old client and cannot carry over.`,
        `\n3. Give her the revised section in full, written for the new niche, and offer to lock it.`,
        `\n\nAsk at most one question, and only if you genuinely cannot produce the revision without it.`,
        stepLine,
      ].join("");
    }
    const triggerMsg = { role: "user", content: triggerContent };
    const nextBase = [...messages, triggerMsg];
    setMessages(nextBase);
    setLoading(true);
    const run = async () => {
      try {
        const token = await session?.getToken();
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            moduleId, max_tokens: 3000,   // model is chosen server-side; 3000 covers Sonnet 5's ~30% larger token counts
            isBlueprintRequest: false,
            archetype: userSettings?.archetype || null,
            archetypePair: userSettings?.archetypePair || ARCHETYPE_MAP[userSettings?.archetype] || null,
            messages: (() => {
              /* Same boundary as the main send path below. This one matters more
                 if anything: it is the promptTrigger path, which is what a REBUILD
                 fires — the one flow guaranteed to be running against a niche she
                 has just changed. */
              const base = withNicheBoundary(nextBase, activeNiche)
                .map((m) => ({ role: m.role, content: m.content }));
              /* No revise context here on purpose: this is the promptTrigger
                 path, which fires for starting a prompt and for each step of a
                 rebuild. A pending revise belongs to the message SHE types, not
                 to a turn the app fired on her behalf. */
              const lead = buildLeadContexts().map((c) => ({ role: "user", content: c }));
              return [...lead, ...base];
            })(),
          }),
        });
        const data = await res.json();
        const reply = data.content?.find((b) => b.type === "text")?.text || "Let's get started — go ahead and tell me where you are with this.";
        const updated = [...nextBase, { role: "assistant", content: reply }];
        setMessages(updated);
        const clerkUserId = user?.id;
        if (clerkUserId) {
          await saveMessageToSupabase(clerkUserId, moduleId, "user", triggerContent);
          await saveMessageToSupabase(clerkUserId, moduleId, "assistant", reply);
        }
      } catch {
        const msg = navigator.onLine ? "Connection issue. Please try again." : "It looks like you're offline. Check your wifi or mobile connection, then try again.";
        setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
      }
      setLoading(false);
    };
    run();
    /* `loading` is in here as a retry, not as a real input. A rebuild fires the
       next section's trigger from handleOutputLocked, which runs inside the reply
       that did the locking — so loading is still true and the guard above bails.
       With only promptTrigger in the deps the effect would never look again and
       the run would stall one section in. */
  }, [promptTrigger, loading]);
  const getSupabase = useSupabase();

  useEffect(() => {
    if (moduleId !== "chat" || chatTab !== "review" || reviewSubsLoaded || !user?.id) return;
    const loadSubs = async () => {
      try {
        const sb = await getSupabase();
        const { data, error } = await sb
          .from("testimonials")
          .select("id, response, stars, created_at")
          .eq("clerk_user_id", user.id)
          .eq("trigger", "chat_review")
          .order("created_at", { ascending: false });
        if (!error && Array.isArray(data)) setReviewSubmissions(data.map(submissionOf));
      } catch (err) {
        console.error("Failed to load testimonials:", err);
      }
      setReviewSubsLoaded(true);
    };
    loadSubs();
  }, [chatTab, reviewSubsLoaded, user?.id]);

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  /* Which module has already had its pending card rebuilt this session. */
  const rehydratedRef = useRef(null);
  const meta = MODULE_META[moduleId] || MODULE_META.chat;
  const isAskBrill = moduleId === "chat";
  /* With chats on (askChat), Ask Anything, Program and Mindset are kinds of
     chat and all read the chat's own thread; only Testimonials keeps its own. */
  const askModeTab = !!askChat && isAskBrill && chatTab !== "review";
  const activeMessages    = !isAskBrill || askModeTab ? messages    : chatTab === "faq"     ? faqMessages     : chatTab === "mindset" ? mindsetMessages     : chatTab === "review" ? reviewMessages     : messages;
  const activeSetMessages = !isAskBrill || askModeTab ? setMessages : chatTab === "faq"     ? setFaqMessages  : chatTab === "mindset" ? setMindsetMessages  : chatTab === "review" ? setReviewMessages  : setMessages;
  const activeModuleKey   = !isAskBrill ? moduleId    : chatTab === "review" ? "chat_review" : MODES[modeOfTab(chatTab)].moduleKey;
  /* Ask Anything with chats (askChat is null until 0042 has run). `askHome` is
     a new chat she has not started — the Welcome back screen. */
  const askHome = askModeTab && askChat.key == null && (messages || []).length === 0;
  const askNamed = askModeTab && askChat.key != null;
  const typedExample = useTypedExamples(askHome && chatTab === "general" && !input);
  const [titleDraft, setTitleDraft] = useState(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [activeMessages, loading]);

  /* One pass over the transcript, not one per message per render — and it has
     to be a pass, because "does this line need a date" depends on the message
     before it. Parallel to activeMessages, so the loop reads it by index. */
  const stamps = useMemo(() => messageStamps(activeMessages), [activeMessages]);

  /* ── A proposed lock survives a refresh ──────────────────────────────────
   *
   * `pendingLock` was built only as a reply streamed in, and lived nowhere else.
   * So a refresh destroyed it, and there was no way back: the transcript still
   * carried Brill's "✓ LOCKED:" claim, storage held nothing, the section read
   * "Not saved" with only view and revise beside it, and Brill would not
   * re-offer because it believed the job was done — "there's nothing new to lock
   * right now". A live client sat in exactly that dead end for a morning
   * (2026-09-01), typing "lock" at a card that no longer existed.
   *
   * NO SCHEMA FOR THIS. A `status` column was the obvious answer and it is too
   * heavy: the block is ALREADY in her transcript, so the card can simply be
   * rebuilt from it. Nothing new stores anything, and `outputs` keeps the rule
   * that took a week to establish — a row in it IS a lock.
   *
   * ONLY THE LAST ASSISTANT MESSAGE, which is the whole reason this is safe.
   * `c9e32bc` deliberately reacted at CLAIM time rather than scanning on open,
   * because scanning a transcript would have surfaced 49 stale cards on that
   * client's account. Reading one message caps this at a single card, for the
   * conversation she is looking at, and `resolveLocks` drops anything whose
   * content already matches what is stored — so a section she has locked cannot
   * produce one at all.
   *
   * ONCE PER MODULE, tracked by ref: without that, pressing "Keep chatting"
   * and coming back would put the card straight back in front of her, which is
   * the nagging this product does not do.
   */
  useEffect(() => {
    if (isAskBrill || !outputsLoaded || loading) return;
    if (rehydratedRef.current === moduleId) return;
    rehydratedRef.current = moduleId;
    const msgs = messages || [];
    let lastAssistant = null;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i]?.role === "assistant") { lastAssistant = msgs[i]; break; }
    }
    if (!lastAssistant?.content) return;
    const { sections } = resolveLocks(
      lastAssistant.content, parseOutputs(msgs), savedOutputs || {}, moduleId);
    if (sections.length) setPendingLock({ sections });
  }, [moduleId, outputsLoaded, loading, isAskBrill, messages, savedOutputs]);

  useEffect(() => {
    if (!reviseFocusTrigger) return;
    const t = setTimeout(() => textareaRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, [reviseFocusTrigger]);

  /* `conversationId` is only passed for an Ask Brill chat (utils/askBrillChats.js);
     every other conversation keeps the id it has always had. */
  const saveMessageToSupabase = async (clerkUserId, moduleKey, role, content, conversationId) => {
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("messages").insert({
        clerk_user_id: clerkUserId,
        conversation_id: conversationId || `${clerkUserId}-${moduleKey}`,
        role,
        content,
        module_key: moduleKey,
      });
      if (error) throw error;
      setSaveWarning(false);
    } catch (err) {
      console.error("Failed to save message:", err);
      setSaveWarning(true);
      setTimeout(() => setSaveWarning(false), 8000);
    }
  };

  /* Both ways in save through here. It reports false on ANY failure — a thrown
     error or Supabase's quiet `{ error }` — and the screen says so in amber,
     because "Submitted" and a lost testimonial used to look identical. */
  const saveTestimonial = async (fields) => {
    const row = testimonialRow({ userId: user?.id, ...fields });
    if (!row) return false;
    try {
      const sb = await getSupabase();
      const { data, error } = await sb.from("testimonials").insert(row).select("id, response, stars, created_at").single();
      if (error || !data) throw error || new Error("no row returned");
      setReviewSubmissions(prev => [submissionOf(data), ...prev]);
      setReviewSaveFailed(false);
      setReviewWriting(false);
      return true;
    } catch (err) {
      console.error("Failed to save testimonial:", err);
      setReviewSaveFailed(true);
      return false;
    }
  };

  const submitQuickReview = async () => {
    if (!reviewQuickStars || reviewQuickSubmitting) return;
    setReviewQuickSubmitting(true);
    const ok = await saveTestimonial({ type: "quick_review", stars: reviewQuickStars, quote: reviewQuickText.trim() });
    if (ok) { setReviewQuickStars(0); setReviewQuickText(""); }
    setReviewQuickSubmitting(false);
  };

  const submitPendingReview = async () => {
    if (!reviewPending || !reviewPendingStars || reviewQuickSubmitting) return;
    setReviewQuickSubmitting(true);
    const ok = await saveTestimonial({ type: "chat_review", stars: reviewPendingStars, ...reviewPending });
    if (ok) {
      setReviewPending(null);
      setReviewPendingStars(0);
      setTimeout(() => setReviewMessages([]), 1800);
    }
    setReviewQuickSubmitting(false);
  };

  /* The silent repair: Brill said a section was locked but wrote the content as
     prose instead of wrapping it in the OUTPUT markers, so nothing could be
     saved. She can SEE the new version on screen, so surfacing a failure to her
     would be nonsense — the mistake is Brill's formatting, and so is the fix.
     Runs only once loading has cleared, so it is a normal turn rather than a
     racing one. `repairedRef` has already claimed these names, so this can fire
     at most once per section per module. */
  useEffect(() => {
    if (!pendingRepair || loading) return;
    const names = pendingRepair.map((n) => `"${n}"`).join(", ");
    setPendingRepair(null);
    /* Through a ref, so this effect depends only on the two things that should
       actually retrigger it. `send` is redefined every render, and depending on
       it here would re-fire the repair on any unrelated state change. */
    sendRef.current?.(
      `[PROMPT_START:relock] You marked ${names} as locked, but did not wrap the content in `
      + `<<<OUTPUT_START:section name>>> ... <<<OUTPUT_END>>> markers, so nothing was saved. `
      + `Output the FULL final version of ${names} again now, wrapped in those markers exactly, `
      + `with the ✓ LOCKED line. Do not change the wording you already wrote, do not summarise it, `
      + `and do not ask her anything — she has already seen it and this is a formatting repair.`
    );
  }, [pendingRepair, loading]);

  /* ── Saying it should work as well as pressing it ────────────────────────
   *
   * A live client was AFRAID OF THE BUTTONS. She did not press Ready to lock,
   * did not use the locked-content view, did not touch the sidebar to change
   * module. She scrolled back through the chat to find her own work. What she
   * DID do was type — "yes lock it", "let's move on to my offer" — because the
   * conversation is the part of this product she trusts.
   *
   * So the answer is not a louder card. It is that the words she already uses
   * do the thing the button does.
   *
   * DELIBERATELY NARROW. Only fires when a lock is already pending, and only on
   * an explicit lock verb — never on a bare "yes", which could be answering
   * something else entirely, and never when she is qualifying ("yes, but change
   * the opening"). A false lock costs her a section she did not agree to, which
   * is the complaint this whole area exists to answer; a false miss costs her
   * one more sentence. */
  /* The matcher moved to `readLockReply` in utils/messages.js on 2026-09-01, so
     it could be tested against real phrases rather than reasoned about. What was
     here matched lock-or-save anywhere within eight words, which confirmed "no
     don't lock it" — its negation list had `dont` and `do not` but not `don't` —
     and also "how do I save this?". It missed the other direction too: Brill's
     guides say "Say yes to lock it in" in six places and bare "yes" matched
     nothing. See readLockReply.test.js, which is the table of real phrases. */

  /* ── Nothing offers her a door while something waits on her word ─────────
   *
   * THE TRAP, found twice by hand (Ideation, then a Decoder revise). A lock
   * card asking "shall I lock this?" and a move-on offer saying "your statement
   * is locked, now we build the offer" render at the same moment, one under the
   * other. It reads as Brill contradicting itself — and it is a fork where one
   * branch silently loses work: she takes the exit, the pending lock is never
   * resolved, and the section she agreed to never lands. Jenn's failure in a
   * new costume.
   *
   * Revise has the same shape for a different reason. Completion is read out of
   * the transcript, and a `LOCKED:` line can never be un-said — so opening a
   * revision leaves every "you're finished here" affordance standing.
   *
   * IT IS `reviseOpen`, NOT `reviseTarget`. `reviseTarget` is spent the moment
   * she sends — it exists to inject one instruction into one message — so
   * gating on it suppressed the exits for exactly one keystroke. She pressed
   * Revise, typed "let's change it", and "Let's go to Offer Ideation" was back
   * underneath Brill asking her what to change. `reviseOpen` stays set until the
   * revision locks or she starts something else. Two different questions.
   *
   * ONE FLAG, not three copies of the condition, so the next affordance added
   * below inherits the rule instead of repeating the bug. */
  /* `moduleOffer` joined them 2026-08-30. When she types "let's move on to my
     offer" a card appears offering that door — and the next-section button and
     the transition card were free to render underneath it, so asking to leave
     produced three doors at once and a "Start <next section>" invitation to do
     the opposite of what she just asked for. Her words: it should not give the
     next prompt in that module. Same fork as 292b62d, third costume. */
  /* `brandAsk` joined them 2026-10-12: after a brand lock, "update Me?" is a
     question she answers before the next section's button competes with it. */
  const awaitingHerWord = !!pendingLock || !!reviseOpen || !!moduleOffer || !!brandAsk;

  /* ── "Let's move on to my offer" ─────────────────────────────────────────
   *
   * Brill cannot change module. Only the nav can. So when a live client typed
   * exactly that, the app kept her in My Market and Brill carried on asking My
   * Market's questions — in the room she had just asked to leave. She never
   * used the sidebar; she was afraid of the buttons and worked entirely by
   * typing.
   *
   * This does not move her. It offers the door, in the place she is already
   * looking, and her message still goes to Brill so it can answer her rather
   * than being talked over.
   *
   * Needs an intent verb with a module NAME right after it: "my offer needs
   * work" is not a request to navigate, and neither is "my kids or my clients
   * … open space", which put this card under her My Vision answer in a demo
   * (2026-09-24). See utils/moduleIntent.js. */

  const send = async (overrideText) => {
    const text = overrideText || input.trim();
    if (!text || loading) return;
    /* HER CONVERSATION DID NOT LOAD, SO NOTHING SENDS (security batch 6,
       2026-10-22). Brill would answer as if the chat were empty — start the
       module again, re-ask what she already answered — and that reply would be
       saved on top of the history she cannot see. Every way in comes through
       here (Send, Enter, a start-prompt, a quick button), so this one line
       covers them; the banner says why and offers Try again. */
    if (historyLoadFailed) return;
    /* TYPING NO LONGER DISMISSES THE PENDING LOCK (2026-08-27).
     *
     * This used to be `setPendingLock(null)`, on the reasoning that "talking on
     * is itself an answer — whatever she says next supersedes the offer." That
     * assumed talking on meant DECLINING. It cost a live client most of a
     * module's work, because she was CONSENTING:
     *
     *   Brill proposes a section, the card appears.
     *   She types "yes lock it" instead of pressing it — the card is destroyed.
     *   Brill replies "✓ LOCKED: <name>" as prose, no fresh block.
     *   resolveLocks resolves the name against content already seen, finds it
     *   unchanged, and correctly refuses to lock it.
     *   The transcript says locked. The database has nothing.
     *
     * Every guard behaved correctly and the work still vanished, because one
     * line read her consent as a refusal. She had also stopped trusting the
     * buttons by then, which is exactly when a quiet card that a keystroke
     * destroys becomes indefensible.
     *
     * Momentum already made this call, in writing (momentum/fields.jsx):
     * "typing does NOT dismiss this — clearing it on a keystroke would be
     * throwing her draft away." Two halves of the product had opposite rules
     * and the wrong one was on the side holding her foundation.
     *
     * It is now cleared by Lock it, by Keep chatting, and by a newer proposal
     * replacing it. Nothing else. */
    /* THREE OUTCOMES NOW, not two — see readLockReply in utils/messages.js.
       The old rule matched lock-or-save anywhere in eight words, so "no don't
       lock it" locked it and so did "how do I save this?". Tightening alone
       would have recreated the failure that cost a live client a morning: a
       lock that quietly does not happen. So the strictness is always visible —
       a near miss puts a line on the card pointing at the button. */
    const lockReply = pendingLock ? readLockReply(text) : LOCK_REPLY.NONE;
    if (lockReply === LOCK_REPLY.CONFIRM) {
      /* She said the thing the button says. Do what the button does — file each
         section to the module that owns it, exactly as the card would — then let
         her message go through so Brill answers her rather than being talked
         over. By the time it replies, "locked" is true. */
      pendingLock.sections.forEach(({ name, content, belongsTo }) =>
        onOutputLocked(belongsTo || moduleId, name, content));
      setPendingLock(null);
      setLockNearMiss(false);
    } else {
      /* Sticky until the card is resolved: she may say two things in a row and
         the prompt should not blink away because the second one was ordinary. */
      setLockNearMiss((prev) => prev || lockReply === LOCK_REPLY.NEAR_MISS);
    }
    /* Captured BEFORE clearing, so this message carries the instruction and the
       next one does not. Revise is a single-shot: she pressed a button, said
       what to change, and that turn is the whole flow. */
    const reviseCtx = buildReviseContext();
    if (reviseCtx) onReviseHandled?.();
    /* Offered, never taken. Moving her without asking would be the same
       overreach as locking without asking. */
    setModuleOffer(moduleFromText(text, MODULE_META, moduleId));
    const isBlueprintRequest = text.toLowerCase().includes("blueprint") && (text.toLowerCase().includes("yes") || text.toLowerCase().includes("generate"));
    /* ASK BRILL CHATS: a first message starts a chat (named from it), and the
       chat is fixed HERE — a reply that lands after she has switched chats
       still goes into the one it answers. */
    const askGeneral = askModeTab;
    const askKeyNow = askGeneral ? (askChat.key ?? askChat.start(text, modeOfTab(chatTab))) : null;
    const put = askGeneral ? askChat.setterFor(askKeyNow) : activeSetMessages;
    const convo = askGeneral ? askChat.conversationIdFor(askKeyNow) : undefined;
    const userMsg = { role: "user", content: text, created_at: new Date().toISOString() };
    const next = [...activeMessages, userMsg];
    put(next);
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    setLoading(true);

    const clerkUserId = user?.id;
    if (clerkUserId) await saveMessageToSupabase(clerkUserId, activeModuleKey, "user", text, convo);

    // Blueprint requests don't need an API call — open the panel directly
    if (isBlueprintRequest && moduleId !== "chat") {
      const confirmMsg = { role: "assistant", content: `Your ${meta.label} Blueprint is ready — opening it now.` };
      put([...next, confirmMsg]);
      if (clerkUserId) await saveMessageToSupabase(clerkUserId, activeModuleKey, "assistant", confirmMsg.content, convo);
      onBlueprintReady(moduleId);
      onViewBlueprint(moduleId);
      setLoading(false);
      return;
    }

    try {
      const prompts = MODULE_PROMPTS[moduleId] || [];
      const parsedOutputs = parseOutputs(next);
      const token = await session?.getToken();

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          moduleId: activeModuleKey,
          max_tokens: 3000,   // model is chosen server-side; 3000 covers Sonnet 5's ~30% larger token counts
          isBlueprintRequest: false,
          moduleLabel: meta.label,
          archetype: userSettings?.archetype || null,
          archetypePair: userSettings?.archetypePair || ARCHETYPE_MAP[userSettings?.archetype] || null,
          messages: (() => {
            /* The boundary goes in BEFORE the map, because that is where the
               timestamps still exist — the map is what strips them. A coach who
               pivoted mid-module gets a line drawn at the pivot; everyone else
               gets exactly what they got before. */
            const base = withNicheBoundary(next, activeNiche)
              .map((m) => ({ role: m.role, content: m.content }));
            /* Additive, not an alternative — she still needs her niche context
               while rewording a section. */
            const lead = [...buildLeadContexts(), reviseCtx].filter(Boolean)
              .map((c) => ({ role: "user", content: c }));
            return [...lead, ...base];
          })(),
        }),
      });
      const data = await res.json();
      if (res.status === 429) {
        const limitMsg = data?.error || "You've reached a usage limit — please try again later.";
        put([...next, { role: "assistant", content: `${limitMsg}\n\nIn the meantime, you can review your locked outputs in the sidebar, or visit the **[Community](https://portal.tevrawood.com/communities/groups/business-brilliance-collab/home)** if you have questions.` }]);
        setLoading(false);
        return;
      }
      /* 402 (out of credits — api/_lib/credits.js), 413 (too big to send) and
         503 (usage could not be checked) carry their own words. Without this they read "Something went
         wrong — try again", and trying again is the wrong advice for both. */
      if (res.status === 402 || res.status === 403 || res.status === 413 || res.status === 503) {
        put([...next, { role: "assistant", content: data?.error || "You don't have access to this module." }]);
        setLoading(false);
        return;
      }
      const reply = data.content?.find((b) => b.type === "text")?.text || "Something went wrong — try again.";
      const updated = [...next, { role: "assistant", content: reply }];
      put(updated);

      if (clerkUserId) await saveMessageToSupabase(clerkUserId, activeModuleKey, "assistant", reply, convo);
      if (askGeneral) askChat.touch(askKeyNow);

      if (isAskBrill && chatTab === "review") {
        /* It does NOT save here. It waits under the chat for her stars
           (submitPendingReview) — it used to save as five she never chose. */
        const drafted = parseTestimonialBlock(reply);
        if (drafted) { setReviewPending(drafted); setReviewPendingStars(0); setReviewSaveFailed(false); }
      }

      if (!isAskBrill && onOutputLocked) {
        const allMsgs = [...next, { role: "assistant", content: reply }];
        const parsed = parseOutputs(allMsgs);
        /* `parsed` still spans the conversation, because the legitimate flow puts
           the block in one message and the marker in the next. What changed is
           that content identical to what is already saved no longer counts as a
           lock — see resolveLocks in utils/messages.js for the bug that caused. */
        /* `moduleId` is new here, and it is what stops a section locking into
           the wrong module. See belongsElsewhere in utils/messages.js — a live
           client did most of My Voice and part of My Offer inside her My Market
           chat, and all of it stored under `market` where nothing looks. */
        const { sections, unchanged, missing } = resolveLocks(reply, parsed, savedOutputs || {}, moduleId);

        /* Brill asking and locking in the same breath used to lock anyway, which
           is the thing that felt like it went behind her back. It no longer
           commits — but it doesn't silently swallow the lock either, because
           then Brill has announced the section is locked and nothing appears.
           She gets the decision it was pretending to offer her. */
        /* EVERY proposed lock waits for her now.
           It used to commit unless Brill happened to ask a question in the same
           reply, which meant the guarantee "a lock is her word" held only by
           accident of phrasing. It is the rule in Momentum and it is the rule
           here: Brill proposes, she locks. */
        if (sections.length) setPendingLock({ sections });

        /* Brill said locked and wrote nothing new — the wrappers were missed and
           the text went out as prose. She can SEE the new version on screen, so
           telling her something failed would be nonsense; the repair is Brill's
           to make, not hers. Ask for that one section again, silently.
           Once per section per session: a second failure means something is wrong
           that another identical request will not fix, and a loop here costs her
           money on every pass. */
        const broken = [...unchanged, ...missing].filter((n) => !repairedRef.current.has(n));
        if (broken.length) {
          broken.forEach((n) => repairedRef.current.add(n));
          /* Queued rather than sent from here. `setLoading(false)` for THIS reply
             runs after the try/catch below, so sending now would either be
             refused by send()'s own loading guard or slip past it on a stale
             closure value — working by accident is not the same as working. The
             effect below fires it once loading has actually cleared. */
          setPendingRepair(broken);
        }
      }
    } catch {
      const msg = navigator.onLine
        ? "Connection issue. Please try again."
        : "It looks like you're offline. Check your wifi or mobile connection, then try again.";
      put([...next, { role: "assistant", content: msg }]);
    }
    setLoading(false);
  };
  sendRef.current = send;

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  /* THE ONE COMPOSER. On the new-chat screen it sits up under the greeting
     (her mockup: "the chat window much higher"), in a card, so it is drawn
     borderless there; once a chat has started it is back in the bar below. One
     element in one place at a time, so the textarea ref is never shared. */
  const renderComposer = (home) => (
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleTextareaChange}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !isMobile) { e.preventDefault(); send(); } }}
          onFocus={() => setTextareaFocused(true)}
          onBlur={() => setTextareaFocused(false)}
          placeholder={!askHome ? "Type your message…"
                : MODES[modeOfTab(chatTab)]?.placeholder || typedExample || " "}
          style={{
            flex: 1, resize: "none", borderRadius: 10,
            border: home ? "none" : textareaFocused ? "1.5px solid var(--accent)" : "1.5px solid var(--border-strong)",
            boxShadow: home ? "none" : textareaFocused ? "0 0 0 3px var(--focus-ring)" : "none",
            padding: "11px 13px", fontSize: 16, outline: "none",
            minHeight: home ? 64 : 46, maxHeight: 120, fontFamily: "inherit", lineHeight: 1.5,
            background: home ? "transparent" : "var(--input-bg)", color: "var(--text-primary)",
            transition: "border-color 0.15s, box-shadow 0.15s",
          }}
          rows={home ? 2 : 1}
        />
        {/* Save that thought — the main Ask Brill chat only (her call): not
            in a module, where the box holds answers to Brill's questions, and
            not in Ask about the program, Mindset reset or a testimonial. */}
        {isAskBrill && chatTab === "general" && (
          <SaveThought text={input} isMobile={isMobile}
            onSaved={(saved) => {
              setInput(""); if (textareaRef.current) textareaRef.current.style.height = "auto";
              setLibraryStart({ kind: saved.kind, id: saved.id }); setLibraryOpen(true);
            }} />
        )}
        <button onClick={() => send()} disabled={!input.trim() || loading || historyLoadFailed} style={{
          background: input.trim() && !loading && !historyLoadFailed ? "var(--you-mark)" : "var(--btn-off-bg)",
          border: "none", borderRadius: 10,
          padding: isMobile ? "12px 13px" : "11px 16px",
          cursor: input.trim() && !loading && !historyLoadFailed ? "pointer" : "not-allowed",
          display: "flex", alignItems: "center", gap: 5,
          color: input.trim() && !loading && !historyLoadFailed ? "var(--you-mark-text)" : "var(--btn-off-text)",
          fontSize: 13, fontWeight: 600, flexShrink: 0,
          boxShadow: input.trim() && !loading && !historyLoadFailed ? "var(--you-glow)" : "none",
        }}>
          <Send size={15} />
          {!isMobile && <span>Send</span>}
        </button>
      </div>
  );

  const renderMessage = (content) => renderMarkdown(cleanMessageContent(content));
  const [libraryOpen, setLibraryOpen] = useState(false);
  /* What the Library opens on: null from the header button, the item just
     saved from Save that thought (her ask: see it land, ✕ out of it). */
  const [libraryStart, setLibraryStart] = useState(null);
  /* Coach Library cards she has answered this visit (Save or Keep chatting),
     by chat and position. After a refresh an unanswered-looking card comes
     back; Save then opens the item she already has rather than saving twice. */
  const [libraryHandled, setLibraryHandled] = useState([]);
  const { panelMode } = usePanelMode() || {};
  const moduleBackdrop = ACCELERATOR_MODULES.includes(moduleId) || IDEATION_MODULES.includes(moduleId);
  const closeLibrary = useCallback(() => { setLibraryOpen(false); setLibraryStart(null); }, []);

  return (
    <div style={{ display: "flex", flex: 1, height: "100%", overflow: "hidden" }}>
      <div style={{ display: "flex", flexDirection: "column", flex: 1, background: isAskBrill ? "var(--ask-bg)" : "var(--bg-primary)", minWidth: 0, height: "100%", overflow: "hidden" }}>
        <div style={{
          background: "var(--chrome-bg)", padding: isMobile ? "13px 14px" : "17px 22px",
          borderBottom: `3px solid ${meta.color}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {isMobile && (
              <button onClick={onMenuOpen} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--chrome-text)", padding: "10px 14px 10px 6px", display: "flex", alignItems: "center", minWidth: 44, minHeight: 44, flexShrink: 0 }}>
                <Menu size={24} />
              </button>
            )}
            <div>
              <h2 style={{ color: "var(--chrome-text)", margin: 0, fontSize: isMobile ? 15 : 16, fontWeight: 700 }}>{meta.label}</h2>
              {!isMobile && <p style={{ color: "var(--chrome-accent)", margin: "3px 0 0", fontSize: 11, opacity: 0.75 }}>Your business brilliance thinking partner</p>}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {rightPanel && (
              <button onClick={onClosePanel} style={{ display: "flex", alignItems: "center", gap: 5, background: "var(--chrome-quiet)", border: "none", borderRadius: 7, padding: "6px 10px", cursor: "pointer", color: "var(--chrome-muted)", fontSize: 11 }}>
                <X size={12} />
                {!isMobile && <span>Close panel</span>}
              </button>
            )}
            {/* The Library, for every tier (her call, 2026-10-09) — the same
                button Momentum has, opening over the chat. LibraryOverlay.jsx. */}
            <LibraryButton open={libraryOpen} onClick={() => setLibraryOpen(true)} />
            <PanelModeToggle isMobile={isMobile} />
          </div>
        </div>
        {libraryOpen && <LibraryOverlay isMobile={isMobile} onClose={closeLibrary} start={libraryStart} />}

        {isAskBrill && !askChat && (
          <div style={{
            background: "var(--bg-surface)", borderBottom: "1px solid var(--border-hairline)",
            padding: "8px 16px", display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap",
          }}>
            {[
              { id: "general",  label: "Ask Anything",    icon: MessageSquare },
              { id: "faq",      label: "Program FAQs",    icon: BookOpen      },
              { id: "mindset",  label: "Mindset Support", icon: Lightbulb     },
              { id: "review",   label: "Testimonials",    icon: Star          },
            ].map(({ id, label, icon: TabIcon }) => (
              <button
                key={id}
                onClick={() => setChatTab(id)}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "6px 13px", borderRadius: 20, border: "none",
                  background: chatTab === id ? "var(--select-bg)" : "var(--bg-sunken)",
                  color: chatTab === id ? "var(--select-text)" : "var(--text-secondary)",
                  fontSize: 12, fontWeight: chatTab === id ? 600 : 400,
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                <TabIcon size={12} />
                {label}
              </button>
            ))}
          </div>
        )}

        {askNamed && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
            padding: isMobile ? "6px 10px" : `8px ${COLUMN_GUTTER}`,
            borderBottom: "1px solid var(--border-hairline)",
          }}>
            {askChat.key === EARLIER ? (
              <h3 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 500, color: "var(--text-primary)", padding: "6px 0" }}>{EARLIER_TITLE}</h3>
            ) : (
              <input
                aria-label="Chat name"
                value={titleDraft ?? askChat.title ?? ""}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => { if (titleDraft != null) askChat.onRename(askChat.key, titleDraft); setTitleDraft(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setTitleDraft(null); e.currentTarget.blur(); } }}
                style={{
                  flex: 1, minWidth: 0, fontSize: 15, fontWeight: 500, color: "var(--text-primary)",
                  background: "transparent", border: "1px solid transparent", borderRadius: 8,
                  padding: "6px 8px", marginLeft: -8, outline: "none", fontFamily: "inherit",
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-line)"; }}
                onBlurCapture={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
              />
            )}
            {askChat.mode && askChat.mode !== "chat" && (
              <span style={{ fontSize: 12, color: "var(--text-secondary)", background: "var(--bg-sunken)", borderRadius: 999, padding: "3px 10px", flexShrink: 0 }}>{MODES[askChat.mode]?.label}</span>
            )}
            <button
              onClick={() => askChat.onDelete(askChat.key, askChat.key === EARLIER ? EARLIER_TITLE : (askChat.title || "this chat"))}
              aria-label="Delete this chat" title="Delete this chat"
              style={{ width: 36, height: 36, border: "none", background: "transparent", borderRadius: 8, color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        )}
        {askChat && isAskBrill && chatTab === "review" && (
          <div style={{ padding: isMobile ? "6px 10px" : `8px ${COLUMN_GUTTER}`, borderBottom: "1px solid var(--border-hairline)", flexShrink: 0 }}>
            <button onClick={() => setChatTab("general")} style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36, border: "none", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
              <ChevronRight size={14} style={{ transform: "rotate(180deg)" }} /> Back to Ask Brill
            </button>
          </div>
        )}

        {/* BEHIND THE ACCELERATOR AND IDEATION CHATS (her ask, 2026-10-22: "let's
            see if it looks too busy"): Brill's lady at the bottom right and the
            big leaf top left, in both modes, as on Ask Brill's home. Neither
            scrolls with the messages, and both sit under them. */}
        <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
        {moduleBackdrop && (
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
            <div style={{ position: "absolute", left: isMobile ? -170 : -260, top: isMobile ? -170 : -250 }}>
              <BrillMark size={isMobile ? 360 : 620} />
            </div>
            {!isMobile && (
              <div style={{ position: "absolute", right: -30, bottom: 0 }}>
                {/* Fainter than on Ask Brill's home in LIGHT mode: here she sits
                    under text, and at the home screen's 0.28 she read too dark
                    (her read, 2026-10-22). Dark mode keeps its own. */}
                <AskBrillFigure width={340} fade={panelMode === "dark" ? undefined : 0.14} />
              </div>
            )}
          </div>
        )}
        <div style={{
          /* The centred column is for CHATTING. The new-chat screen uses the whole
             width (her call), so the big leaf sits in the corner of the page and
             not at the edge of an invisible column. */
          position: "relative", zIndex: 1,
          flex: 1, overflowY: "auto", padding: askHome ? 0 : isMobile ? "12px 10px" : `16px ${COLUMN_GUTTER}`,
          display: "flex", flexDirection: "column", gap: 12,
          paddingBottom: askHome ? 0 : "16px",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}>
          {historyLoadFailed && (
            <div style={{ ...NOTICE, padding: "9px 12px", flexShrink: 0 }}>
              <AlertTriangle size={13} color="var(--warn-line)" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1 }}>Brill couldn't load your conversation, so sending is paused until it can see it. Nothing is lost.</span>
              {onRetryHistory && (
                <button type="button" onClick={onRetryHistory}
                  style={{ background: "none", border: "none", padding: 0, color: "inherit", fontSize: "inherit", fontWeight: 700, textDecoration: "underline", cursor: "pointer", flexShrink: 0 }}>
                  Try again
                </button>
              )}
            </div>
          )}

          {isAskBrill && chatTab === "review" && reviewSaveFailed && (
            <div style={{ ...NOTICE, padding: "9px 12px", flexShrink: 0 }}>
              <AlertTriangle size={13} color="var(--warn-line)" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1 }}>Your testimonial didn't save. Your words are still here — try again.</span>
            </div>
          )}

          {/* Until her earlier testimonials have loaded, neither screen: the
              form used to flash up and then be swapped for her list. */}
          {isAskBrill && chatTab === "review" && !reviewSubsLoaded && activeMessages.length === 0 && (
            <p style={{ margin: "auto", fontSize: 12, color: "var(--text-muted)" }}>Loading your testimonials…</p>
          )}
          {isAskBrill && chatTab === "review" && reviewSubsLoaded && !reviewWriting && activeMessages.length === 0 && reviewSubmissions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, padding: "24px 4px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <Star size={14} color="#F5A623" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                    {reviewSubmissions.length === 1 ? "1 Testimonial Submitted" : `${reviewSubmissions.length} Testimonials Submitted`}
                  </span>
                </div>
                <button
                  onClick={() => { setReviewWriting(true); setReviewSaveFailed(false); }}
                  style={{ background: "var(--btn-bg)", color: "var(--btn-text)", border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                >
                  + Write Another
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {reviewSubmissions.map((sub, i) => (
                  <div key={i} style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: 12, padding: "16px 18px", boxShadow: "var(--shadow-soft)" }}>
                    {sub.stars && <p style={{ margin: "0 0 6px", fontSize: 14 }}>{"⭐".repeat(sub.stars)}</p>}
                    {sub.quote && <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--text-primary)", lineHeight: 1.6, fontStyle: "italic" }}>"{sub.quote}"</p>}
                    {sub.attribution && (
                      <p style={{ margin: "0 0 4px", fontSize: 11, color: "var(--text-muted)", fontWeight: 500 }}>— {sub.attribution}</p>
                    )}
                    {sub.video && (
                      <p style={{ margin: "0 0 4px", fontSize: 11, color: "var(--text-muted)" }}>Video: {sub.video}</p>
                    )}
                    <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--text-disabled)" }}>
                      {new Date(sub.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAskBrill && activeMessages.length === 0 && !(chatTab === "review" && (!reviewSubsLoaded || (reviewSubmissions.length > 0 && !reviewWriting))) && (() => {
            if (chatTab === "review") {
              return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, padding: "32px 20px" }}>
                  <div style={{ width: "100%", maxWidth: 360 }}>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--accent-quiet)", border: "1px solid var(--accent-line)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                        <Star size={20} color="var(--accent)" />
                      </div>
                      <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>Leave a Testimonial</h3>
                      <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>Shared with Tevra and may be used publicly.</p>
                    </div>

                    <div style={{ display: "flex", gap: 4, justifyContent: "center", marginBottom: 12 }}>
                      {[1,2,3,4,5].map(n => (
                        <button key={n}
                          onClick={() => setReviewQuickStars(n)}
                          onMouseEnter={() => setReviewQuickHover(n)}
                          onMouseLeave={() => setReviewQuickHover(0)}
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 26, padding: 2, opacity: (reviewQuickHover || reviewQuickStars) >= n ? 1 : 0.25, transition: "opacity 0.1s" }}
                        >⭐</button>
                      ))}
                    </div>

                    <textarea
                      value={reviewQuickText}
                      onChange={e => setReviewQuickText(e.target.value)}
                      placeholder="Share your experience… (optional)"
                      style={{ width: "100%", resize: "none", border: "1.5px solid var(--border-strong)", borderRadius: 8, padding: "9px 11px", fontSize: 13, fontFamily: "inherit", lineHeight: 1.5, minHeight: 80, outline: "none", boxSizing: "border-box", marginBottom: 10, background: "var(--input-bg)", color: "var(--text-primary)" }}
                      rows={3}
                    />

                    <button
                      onClick={submitQuickReview}
                      disabled={!reviewQuickStars || reviewQuickSubmitting}
                      style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: reviewQuickStars ? "var(--btn-bg)" : "var(--btn-off-bg)", color: reviewQuickStars ? "var(--btn-text)" : "var(--btn-off-text)", fontSize: 13, fontWeight: 600, cursor: reviewQuickStars ? "pointer" : "not-allowed", transition: "background 0.15s", marginBottom: 16 }}
                    >
                      {reviewQuickSubmitting ? "Submitting…" : "Submit"}
                    </button>

                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                      <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
                      <span style={{ fontSize: 11, color: "var(--text-disabled)", whiteSpace: "nowrap" }}>or let Brill write it</span>
                      <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
                    </div>

                    <p style={{ margin: "0 0 10px", fontSize: 11, color: "var(--text-muted)", textAlign: "center" }}>Pick a topic and Brill will ask you two questions</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {[
                        { label: "Tevra as a Coach",  topic: "Tevra as a Coach"  },
                        { label: "My Business",        topic: "My Business"        },
                        { label: "The Brill Platform", topic: "The Brill Platform" },
                        { label: "Operations",         topic: "Operations"         },
                      ].map(({ label, topic }) => (
                        <button
                          key={topic}
                          onClick={() => send(topic)}
                          style={{ flex: "1 1 calc(50% - 4px)", padding: "9px 8px", borderRadius: 8, border: "1.5px solid var(--accent-line)", background: "var(--surface-grey)", color: "var(--text-primary)", fontSize: 12, fontWeight: 500, cursor: "pointer", textAlign: "center", transition: "border-color 0.15s, background 0.15s" }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.background = "var(--accent-quiet)"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--accent-line)"; e.currentTarget.style.background = "var(--surface-grey)"; }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            /* Each tab has an identity tone. `quiet` and `line` were `${color}18`
               and `${color}33` — spelled out here because a token can't take a
               hex-alpha suffix. The blue and purple are light enough to read on
               either canvas, so only `general` is tokenised. */
            const tabConfig = {
              general: {
                icon: MessageSquare,
                tone: "var(--accent)", quiet: "var(--accent-quiet)", line: "var(--accent-line)",
                title: "Ask Anything",
                description: "Think something through, get unstuck, or ask me anything that comes up between sessions.",
                starters: [
                  "I'm not sure how to talk about what I do",
                  "I feel like I'm going in circles on something",
                  "Help me think through a decision I'm facing",
                ],
              },
              faq: {
                icon: BookOpen,
                tone: "#7BAFD4", quiet: "rgba(123,175,212,0.09)", line: "rgba(123,175,212,0.20)",
                title: "Program FAQs",
                description: "Common questions about how the program works, what to expect, and how to navigate your journey.",
                starters: [
                  "How long does each module take?",
                  "What if I get stuck?",
                  "How do the 1:1 calls work?",
                  "Can I skip a module?",
                ],
              },
              mindset: {
                icon: Lightbulb,
                tone: "#A78BFA", quiet: "rgba(167,139,250,0.09)", line: "rgba(167,139,250,0.20)",
                title: "Mindset Support",
                description: "Working through a block? Feeling stuck or scared? This is the space for that conversation.",
                starters: [
                  "I'm feeling like an imposter right now",
                  "I'm afraid to put my prices out there",
                  "I keep stopping before I start",
                  "I don't feel ready to be visible",
                ],
              },
            };
            if (askHome) {
              return (
                <AskBrillHome
                  firstName={askChat.firstName}
                  leftOff={askChat.leftOff}
                  isMobile={isMobile}
                  onSwitchModule={onSwitchModule}
                  onStartSection={askChat.onStartSection}
                  /* What the tabs used to hold now lives here (her change): the
                     kind of chat she starts, and the testimonial form. */
                  mode={modeOfTab(chatTab)}
                  onMode={(m) => setChatTab(tabOfMode(m))}
                  onTestimonial={() => setChatTab("review")}
                  problem={askChat.problem}
                  composer={renderComposer(true)}
                />
              );
            }
            /* A coach exists only as a chat (askChat), so it never reaches this
               old tabbed screen — but if it did, it is general Brill's screen. */
            const cfg = tabConfig[chatTab] || tabConfig.general;
            const TabIcon = cfg.icon;
            return (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, padding: "32px 20px", textAlign: "center" }}>
                <div style={{
                  width: 52, height: 52, borderRadius: "50%",
                  background: cfg.quiet, border: `1px solid ${cfg.line}`,
                  display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
                }}>
                  <TabIcon size={20} color={cfg.tone} />
                </div>
                <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>{cfg.title}</h3>
                <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.65, maxWidth: 320 }}>{cfg.description}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 400 }}>
                  {cfg.starters.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      style={{
                        background: "var(--surface-grey)", border: `1px solid ${cfg.line}`,
                        borderRadius: 20, padding: "7px 14px",
                        fontSize: 12, color: "var(--text-primary)", cursor: "pointer",
                        lineHeight: 1.4, textAlign: "left",
                        boxShadow: "var(--shadow-soft)",
                        transition: "border-color 0.15s",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {(Array.isArray(activeMessages) ? activeMessages : []).map((m, i) => {
            const cleaned = cleanMessageContent(m.content);
            /* The marker says WHERE the badge belongs -- which message in the
               transcript it hangs off. STORAGE says what state it is in. Before
               this, a transcript line was the whole answer, which is why a
               rebuild could not clear an old badge and why a section could read
               LOCKED while absent from the database.
               Until her outputs have arrived we keep the old reading, or every
               badge would blink to "not saved" on load. */
            const markerNames = m.role === "assistant" ? extractLockedSections(m.content) : [];
            const lockedNames = markerNames
              .filter((n) => !(pendingLock?.sections || []).some((s2) => s2.name === n))
              /* A section this module does not own gets NO badge here. It was
                 filed to the module that owns it (see belongsElsewhere), so
                 `savedOutputs` — which is this module's — will never contain it,
                 and the badge would read "not saved" the moment after she
                 successfully locked it. This module has no opinion about a
                 section that isn't its own, and saying nothing is the honest
                 version of that. The card already told her where it went. */
              .filter((n) => !belongsElsewhere(n, moduleId));
            const stateOf = (n) => (!outputsLoaded ? LOCK_STATE.LOCKED : sectionLockState({
              name: n,
              savedOutputs,
              stamps: outputStamps?.[moduleId],
              activeNicheId: activeNiche?.id || null,
            }));
            const hasText = !!cleaned;
            const stamp = stamps[i];
            if (!hasText && lockedNames.length === 0) return null;
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                {hasText && (
                  <div style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 8, alignItems: "flex-start", width: "100%" }}>
                    {m.role === "assistant" && (
                      <div style={{ ...BOT_AVATAR, marginTop: 2 }}>
                        <BrillAvatarMark />
                      </div>
                    )}
                    {/* The bubble and its stamp share a column, so the stamp ends
                        level with the BUBBLE's edge rather than the avatar's.
                        maxWidth lives on the column for the same reason. */}
                    <div style={{
                      display: "flex", flexDirection: "column", minWidth: 0,
                      alignItems: m.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: isMobile ? "84%" : BUBBLE_MAX,
                    }}>
                      <div style={{
                        maxWidth: "100%",
                        padding: isMobile ? "10px 12px" : "11px 14px",
                        borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                        background: m.role === "user" ? "var(--you-bg)" : "var(--surface-grey)",
                        color: m.role === "user" ? "var(--you-text)" : "var(--text-primary)",
                        fontSize: isMobile ? 14 : 13, lineHeight: 1.7,
                        boxShadow: m.role === "user" ? "var(--you-glow)" : "var(--shadow-bubble)",
                        wordBreak: "break-word",
                      }}>
                        {renderMessage(m.content)}
                      </div>
                      {stamp && (
                        <div style={{ fontSize: 10.5, color: "var(--you-stamp)", marginTop: 5, whiteSpace: "nowrap" }}>
                          You · {stamp}
                        </div>
                      )}
                    </div>
                    {m.role === "user" && (
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--you-mark)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                        <User size={13} color="var(--you-mark-text)" />
                      </div>
                    )}
                  </div>
                )}
                {lockedNames.map((name, j) => {
                  const sectionContent = savedOutputs?.[name] || parseOutputs(activeMessages.slice(0, i + 1))[name];
                  const lockState = stateOf(name);
                  const showAvatar = j === 0 && !hasText;
                  return (
                    <div key={name} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginTop: 2, ...(showAvatar ? BOT_AVATAR : {}) }}>
                        {showAvatar && <BrillAvatarMark />}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: isMobile ? "84%" : BUBBLE_MAX }}>
                        <button
                          onClick={() => sectionContent && onViewOutput && onViewOutput(moduleId, name, sectionContent)}
                          style={{
                            flex: 1, display: "flex", alignItems: "center", gap: 9,
                            background: lockState === LOCK_STATE.LOCKED ? "var(--accent-quiet)" : "var(--bg-surface)",
                            border: `1px solid ${lockState === LOCK_STATE.LOCKED ? "var(--accent-line)" : "var(--border-strong)"}`,
                            borderRadius: 10, padding: "8px 12px",
                            cursor: sectionContent && onViewOutput ? "pointer" : "default",
                            textAlign: "left", fontFamily: "inherit",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => { if (sectionContent && onViewOutput && lockState === LOCK_STATE.LOCKED) e.currentTarget.style.background = "var(--accent-hover)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = lockState === LOCK_STATE.LOCKED ? "var(--accent-quiet)" : "var(--bg-surface)"; }}
                        >
                          {/* Three states, from storage. "Past locked" is real work
                              built under a niche she has left -- it is not a
                              warning and not a gap, it is something to review,
                              so it goes quiet rather than green. "Not saved" is
                              the honest reading of a marker with no row behind
                              it, which used to render as a confident tick. */}
                          <CheckCircle size={14} style={{ flexShrink: 0 }}
                            color={lockState === LOCK_STATE.LOCKED ? "var(--accent)" : "var(--text-muted)"} />
                          <div>
                            <div style={{
                              fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                              letterSpacing: "0.5px", lineHeight: 1.2,
                              color: lockState === LOCK_STATE.LOCKED ? "var(--accent)" : "var(--text-muted)",
                            }}>
                              {lockState === LOCK_STATE.LOCKED ? "Locked"
                                : lockState === LOCK_STATE.PAST ? "Past locked"
                                  : "Not saved"}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 600, lineHeight: 1.4 }}>{name}</div>
                            {lockState === LOCK_STATE.PAST && (
                              <div style={{ fontSize: 10.5, color: "var(--text-muted)", lineHeight: 1.4, marginTop: 1 }}>
                                built on a niche you have left
                              </div>
                            )}
                          </div>
                          {sectionContent && onViewOutput && <ChevronRight size={12} color="var(--text-disabled)" style={{ marginLeft: "auto", flexShrink: 0 }} />}
                        </button>
                        {onReviseSection && (
                          <button
                            onClick={() => onReviseSection(name)}
                            title="Revise this section"
                            style={{
                              background: "none", border: "1px solid var(--border-strong)", borderRadius: 8,
                              padding: "7px 8px", cursor: "pointer", display: "flex", alignItems: "center",
                              flexShrink: 0, transition: "border-color 0.15s",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-strong)"; }}
                          >
                            <Pencil size={12} color="var(--text-muted)" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {(() => {
            /* getVisiblePrompts, NOT MODULE_PROMPTS (2026-08-30). This is the
               FRONT DOOR — "Get Started" fires prompts[0] — and
               MODULE_PROMPTS.client[0] is "Niche Clarity", retired from My
               Client on 2026-08-27. So the first button a coach pressed in My
               Client started a section that exists nowhere else in the product:
               not in the sidebar, not in the progress count, not in the
               blueprint. Same two-readers bug as the next-section button below,
               one screen earlier and worse. */
            const prompts = getVisiblePrompts(moduleId, messages);
            const isUnstarted = activeMessages.length === 1 && activeMessages[0]?.role === "assistant";
            if (!isUnstarted || prompts.length === 0 || loading || !onStartPrompt) return null;
            return (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", animation: "fadeUp 0.4s ease" }}>
                <div style={BOT_AVATAR}>
                  <BrillAvatarMark />
                </div>
                <button
                  onClick={() => onStartPrompt(moduleId, prompts[0])}
                  style={START_BTN}
                >
                  Get Started
                </button>
              </div>
            );
          })()}
          {/* ── She asked to move, in words ──────────────────────────────────
              Brill cannot change module, so without this her request simply
              disappeared into a reply from the module she wanted to leave.
              Sits with the lock card because that is where she is already
              reading — not in the sidebar, which she never opened. */}
          {/* ── AFTER A BRAND LOCK: update Me? (her rule, 2026-10-12) ─────────
              Set by App when the lock SAVED. Nothing in Me changes until she
              presses; "Keep" leaves Me exactly as it is, and Me → Brand keeps
              naming the difference until she settles it there. */}
          {brandAsk && !loading && (
            <div role="group" aria-label="Update your Brand in Me" style={{
              margin: "2px 0 12px", marginLeft: 38,
              border: "1px solid var(--accent-line)", background: "var(--accent-quiet)",
              borderRadius: 12, padding: "12px 14px",
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <span style={{ flex: 1, minWidth: 200, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                {brandAsk.kind === "colors"
                  ? (brandAsk.mode === "fill" ? <>Add these colors to <b style={{ color: "var(--text-primary)" }}>Me → Brand</b>? Every deck, email and brief will use them.</>
                    : <>These colors are different from your <b style={{ color: "var(--text-primary)" }}>Brand in Me</b>. Update Me to these?</>)
                  : (brandAsk.mode === "fill" ? <>Add <b style={{ color: "var(--text-primary)" }}>{[brandAsk.fonts.heading, brandAsk.fonts.body].filter(Boolean).join(" / ")}</b> to Me → Brand as your fonts?</>
                    : <>These fonts are different from your <b style={{ color: "var(--text-primary)" }}>Brand in Me</b>. Update Me to {[brandAsk.fonts.heading, brandAsk.fonts.body].filter(Boolean).join(" / ")}?</>)}
                {brandAsk.kind === "colors" && (
                  <span style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                    {brandAsk.colors.map((c) => <span key={c.hex} title={`${c.name} #${c.hex}`} style={{ width: 18, height: 18, borderRadius: 4, background: `#${c.hex}`, border: "1px solid var(--border-strong, var(--border))" }} />)}
                  </span>
                )}
              </span>
              <button onClick={() => onBrandAnswer?.(true)}
                style={{ padding: "6px 13px", borderRadius: 7, border: "none", background: "var(--btn-bg)", color: "var(--btn-text)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                {brandAsk.mode === "fill" ? "Add to Me" : "Update Me"}
              </button>
              <button onClick={() => onBrandAnswer?.(false)}
                style={{ padding: "6px 10px", borderRadius: 7, border: "none", background: "transparent", color: "var(--text-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                {brandAsk.mode === "fill" ? "Not now" : "Keep Me as it is"}
              </button>
            </div>
          )}
          {moduleOffer && !loading && (
            <div style={{
              margin: "2px 0 12px", marginLeft: 38,
              border: "1px solid var(--accent-line)", background: "var(--accent-quiet)",
              borderRadius: 12, padding: "12px 14px",
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <span style={{ flex: 1, minWidth: 180, fontSize: 13, color: "var(--text-secondary)" }}>
                Want to go to <b style={{ color: "var(--text-primary)" }}>
                  {MODULE_META[moduleOffer]?.label || moduleOffer}
                </b>? Brill can’t move you — this can.
              </span>
              <button
                onClick={() => { const to = moduleOffer; setModuleOffer(null); onSwitchModule?.(to); }}
                style={{
                  padding: "6px 13px", borderRadius: 7, border: "none",
                  background: "var(--btn-bg)", color: "var(--btn-text)",
                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
                }}
              >
                Take me there
                <ArrowRight size={11} />
              </button>
              <button
                onClick={() => setModuleOffer(null)}
                style={{
                  padding: "6px 10px", borderRadius: 7, border: "none", background: "transparent",
                  color: "var(--text-muted)", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
                }}
              >
                Stay here
              </button>
            </div>
          )}
          {/* ── A coach offers to save something to her Library ──────────────
              Only under the LAST reply: once she types, the moment has passed
              and the card goes with it, like the lock card. */}
          {isAskBrill && !loading && (() => {
            const list = Array.isArray(activeMessages) ? activeMessages : [];
            const last = list[list.length - 1];
            const item = last?.role === "assistant" ? parseLibraryProposal(last.content) : null;
            const key = `${askChat?.key ?? "-"}:${list.length}`;
            if (!item || libraryHandled.includes(key)) return null;
            return (
              <LibraryProposal item={item} isMobile={isMobile}
                onSaved={(saved) => {
                  setLibraryHandled((h) => [...h, key]);
                  setLibraryStart({ kind: saved.kind, id: saved.id }); setLibraryOpen(true);
                }}
                onDismiss={() => setLibraryHandled((h) => [...h, key])} />
            );
          })()}

          {/* ── Ask Brill offers the tool that builds what she asked for ───── */}
          {isAskBrill && !loading && (() => {
            const list = Array.isArray(activeMessages) ? activeMessages : [];
            const last = list[list.length - 1];
            const tool = last?.role === "assistant" ? toolOffer(last.content, brillTools) : null;
            if (!tool) return null;
            return (
              <div style={{ margin: "2px 0 12px", marginLeft: 38 }}>
                <button onClick={() => onSwitchModule?.(tool.navId)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 14px", borderRadius: 999, border: "1.5px solid var(--accent-line)",
                    background: "transparent", color: "var(--accent)", fontSize: 13, fontWeight: 500,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                  Open {tool.label} <ArrowRight size={13} />
                </button>
              </div>
            );
          })()}

          {/* ── Lock it, or keep talking ─────────────────────────────────────
              Shown when Brill proposed a section and asked her about it in the
              same reply. It used to just lock; the point of this is that a lock
              is her word, not Brill's. Deliberately quiet — it sits under the
              message rather than interrupting, and doing nothing is a valid
              answer, because typing anything at all clears it. */}
          {pendingLock && !loading && (
            /* The same card shape Momentum uses for a draft waiting on her word,
               so a lock reads as the same moment on both sides of the product.
               What it deliberately does NOT do is repeat the copy: Momentum's
               card is the only place its draft exists, whereas here Brill's
               version is in the message directly above, and showing it twice
               would push the buttons off the screen. */
            <div style={{
              margin: "2px 0 12px", marginLeft: 38,
              border: "1px dashed var(--border-strong)", background: "var(--bg-surface)",
              borderRadius: 12, padding: "12px 14px",
            }}>
              <div style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: "0.11em",
                textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 7,
              }}>
                Ready to lock
              </div>
              {/* SHOW IT. The output block is stripped from the transcript by
                  cleanMessageContent, so "here's the complete profile" was
                  followed by nothing at all and the text only existed behind a
                  card she had to click AFTER it was already locked. She cannot
                  be asked to lock something she has not been shown. */}
              {pendingLock.sections.map(({ name, content, belongsTo }) => (
                <div key={name} style={{ marginBottom: 11 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-primary)", marginBottom: 5 }}>
                    {name}
                    {/* Where it is really going. Named on the card rather than
                        after the fact, because "locked!" followed by nothing
                        appearing in this module is the confusing version — and
                        it is the version a live client actually lived through. */}
                    {belongsTo && (
                      <span style={{
                        marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                        textTransform: "uppercase", color: "var(--warn-text)",
                        background: "var(--warn-bg)", border: "1px solid var(--warn-line)",
                        borderRadius: 12, padding: "2px 8px", verticalAlign: "1px",
                      }}>
                        → {MODULE_META[belongsTo]?.label || belongsTo}
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: isMobile ? 13.5 : 12.5, lineHeight: 1.65,
                    color: "var(--text-secondary)",
                    maxHeight: 320, overflowY: "auto",
                    /* Capped and scrollable: a full Ideal Client Profile runs long
                       enough to push the buttons off the screen at the exact
                       moment she needs to press one. */
                  }}>
                    {renderMarkdown(content)}
                  </div>
                </div>
              ))}
              {/* SHE SAID SOMETHING ABOUT LOCKING AND IT WAS NOT A CLEAR YES.
                  This line is what makes the matcher safe to tighten. Every
                  earlier fix in this seam added a way for a lock to quietly not
                  happen; here being strict is always visible, so the worst case
                  is one more click rather than work she thinks is saved and
                  isn't. It also points at the button, which is where we want
                  people going. */}
              {lockNearMiss && (
                <div style={{
                  fontSize: isMobile ? 12.5 : 11.5, lineHeight: 1.55,
                  color: "var(--warn-text)", marginBottom: 8,
                }}>
                  Did you mean to lock this? Press <strong>Lock it</strong> and it’s saved.
                </div>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => {
                  /* Each section goes to the module that OWNS it, not the one
                     she happens to be sitting in. `handleOutputLocked` already
                     takes the module as an argument, so filing it correctly
                     costs nothing — it was only ever passed `moduleId` because
                     nothing had asked the question before. */
                  pendingLock.sections.forEach(({ name, content, belongsTo }) =>
                    onOutputLocked(belongsTo || moduleId, name, content));
                  setPendingLock(null);
                  setLockNearMiss(false);
                            }}
                style={{
                  padding: "6px 13px", borderRadius: 7, border: "none",
                  background: "var(--btn-bg)", color: "var(--btn-text)",
                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 5,
                }}
              >
                <CheckCircle size={11} />
                Lock it
              </button>
              {/* ── "and take me there" ────────────────────────────────────
                  When a proposed section belongs to another module, filing it
                  correctly is only half the answer. She came here to work on
                  this, and the work has just landed somewhere she isn't — so
                  the card offers the door as well as the fact.
                  Only when EVERY pending section shares one destination: a
                  reply that locks two sections into two different modules has
                  no single "there" to go to, and guessing one would be worse
                  than saying nothing. */}
              {(() => {
                /* Only the revise half here, NOT `awaitingHerWord`: this
                   button lives inside the pending-lock card, so gating it on
                   `pendingLock` would delete it outright. Mid-revision it still
                   has to go — it would carry her out of the section she is
                   part-way through rewriting. */
                if (reviseOpen) return null;
                const away = [...new Set(pendingLock.sections.map((s2) => s2.belongsTo).filter(Boolean))];
                if (away.length !== 1 || away[0] === moduleId) return null;
                if (away.length !== pendingLock.sections.length && pendingLock.sections.some((s2) => !s2.belongsTo)) return null;
                const dest = away[0];
                return (
                  <button
                    onClick={() => {
                      pendingLock.sections.forEach(({ name, content, belongsTo }) =>
                        onOutputLocked(belongsTo || moduleId, name, content));
                      setPendingLock(null);
                      setLockNearMiss(false);
                      onSwitchModule?.(dest);
                    }}
                    style={{
                      padding: "6px 13px", borderRadius: 7,
                      border: "1px solid var(--warn-line)", background: "var(--warn-bg)",
                      color: "var(--warn-text)", fontSize: 12, fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", gap: 5,
                    }}
                  >
                    Lock it and go to {MODULE_META[dest]?.label || dest}
                    <ArrowRight size={11} />
                  </button>
                );
              })()}
              <button
                onClick={() => { setPendingLock(null); setLockNearMiss(false); }}
                style={{
                  padding: "6px 13px", borderRadius: 7,
                  border: "1px solid var(--border-strong)", background: "var(--bg-surface)",
                  color: "var(--text-secondary)", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Keep chatting
              </button>
              </div>
            </div>
          )}
          {/* ── On to the next module? ───────────────────────────────────────
              Shown once a rebuild reaches the end of its queue, and only in the
              module she just finished. A pivot usually touches several modules,
              but running them together would be a different promise — she agreed
              to rebuild one. So the next one is offered by name and she says
              when, which is also her chance to stop for the day. */}
          {rebuildNext && !loading && !awaitingHerWord && (
            <div style={{
              display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
              margin: "2px 0 12px", paddingLeft: 38,
            }}>
              <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                Next up — {MODULE_META[rebuildNext.moduleKey]?.label || rebuildNext.moduleKey}
                {rebuildNext.stale?.length ? `, ${rebuildNext.stale.length} ${rebuildNext.stale.length === 1 ? "section" : "sections"}` : ""}
              </span>
              <button
                onClick={() => onRebuildNext(rebuildNext.moduleKey)}
                style={{
                  padding: "6px 13px", borderRadius: 7, border: "none",
                  background: "var(--btn-bg)", color: "var(--btn-text)",
                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 5,
                }}
              >
                Keep going
                <ArrowRight size={11} />
              </button>
              <button
                onClick={onDismissRebuildNext}
                style={{
                  padding: "6px 13px", borderRadius: 7,
                  border: "1px solid var(--border-strong)", background: "var(--bg-surface)",
                  color: "var(--text-secondary)", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Not now
              </button>
            </div>
          )}
          {(() => {
            /* getVisiblePrompts, NOT MODULE_PROMPTS (2026-08-30). Niche Clarity
               was retired from My Client on 2026-08-27 — the niche moved to My
               Mission long ago — and `getVisiblePrompts` is where that lives. The
               sidebar used it and correctly stopped showing the row; this button
               read the raw list and went on offering "Start Niche Clarity →" for
               a section that no longer exists anywhere else in the product. Two
               readers of "what sections does this module have", one of them
               wrong, which is the shape of every bug in this seam. */
            const prompts = getVisiblePrompts(moduleId, messages);
            if (prompts.length === 0 || loading || !onStartPrompt) return null;
            // See `awaitingHerWord`: no exits while a lock or a revise is open.
            if (awaitingHerWord) return null;
            /* NO GATE ON THE TRANSCRIPT (2026-08-27).
             *
             * This used to require the LAST assistant message to carry the lock
             * marker. Two ways that failed, both watched on a real account:
             *
             *  - Brill answers once more after locking and the marker is no
             *    longer the newest thing said, so the button vanishes. A circle:
             *    Brill opens the next section because no button told it to wait,
             *    and the button disappears because Brill opened the next section.
             *  - She leaves the module and comes back. The marker is buried in
             *    history, so there is NOWHERE TO GO. She found her way by
             *    hunting for the section in the sidebar — the chrome she is
             *    least comfortable with, at the moment she is most lost.
             *
             * Where she is up to is a fact about her locked work, not about what
             * Brill last said. So it reads storage, and it survives a reload. */
            /* DONE MEANS STORED, not "Brill said so" and not "stored under the
               niche she is on today". A section waiting on her word is not done.

               IT USED TO MEAN STORED AND CURRENT, and she caught it (2026-09-01):
               My Vision offered "Start Me" when Me was already written — stored
               under a niche she had left — and Family, the section after it, was
               finished too. The next section was Community. Two affordances
               answering "where is she up to" and giving different answers: the
               sidebar ticked Me that morning, this button still offered it.

               THE RULE IS THE "!", NOT THE STAMP — her words: "if Me had an !
               that would make sense, but if you said it was good then it should
               count it as done." `NICHE_SENSITIVITY` marks `vision` as SAFE
               ("about her, a pivot doesn't touch these"), so Me carries an old
               stamp and deliberately no mark. The app had already decided that
               section was fine and the button was still offering to start it.

               So a stored section counts as done UNLESS it is stale in a way
               that actually surfaces to her. `isStale` cannot answer that — it
               only compares stamps — which is why `flagsToCoach` exists.

               THIS ALSO KEEPS HER FROM BEING STRANDED. Reading it as "stored,
               full stop" would make a fully-stale module offer nothing at all:
               no next section, and no transition card either, since that one is
               separately suppressed while anything is stale. My Mission still
               offers its first flagged section, exactly as it does today.

               Falls back to the transcript reading until her outputs land. */
            const pendingNames = new Set((pendingLock?.sections || []).map((s2) => s2.name));
            const completed = !outputsLoaded
              ? new Set([...detectCompletedPrompts(moduleId, activeMessages)].filter((n) => !pendingNames.has(n)))
              : new Set([...sectionProgress({
                /* Same list as the line above, or the count and the offer
                   disagree: a retired section could never be completed, so the
                   module would never read finished. */
                prompts,
                savedOutputs,
                stamps: outputStamps?.[moduleId],
                activeNicheId: activeNiche?.id || null,
              }).built]
                .filter((n) => !pendingNames.has(n))
                .filter((n) => !(isStale(moduleId, n) && flagsToCoach(moduleId, n))));
            if (completed.size === 0 || completed.size >= prompts.length) return null;
            const nextPrompt = prompts.find(p => !completed.has(p));
            if (!nextPrompt) return null;
            return (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", animation: "fadeUp 0.4s ease" }}>
                <div style={BOT_AVATAR}>
                  <BrillAvatarMark />
                </div>
                <button
                  onClick={() => onStartPrompt(moduleId, nextPrompt)}
                  style={START_BTN}
                >
                  Start {nextPrompt} →
                </button>
              </div>
            );
          })()}
          {loading && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={BOT_AVATAR}>
                <BrillAvatarMark />
              </div>
              <div style={{ background: "var(--surface-grey)", padding: "12px 15px", borderRadius: "16px 16px 16px 4px", boxShadow: "var(--shadow-bubble)" }}>
                <div style={{ display: "flex", gap: 4 }}>
                  {[0, 150, 300].map((d) => (
                    <div key={d} style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--accent)", animation: "bounce 1s infinite", animationDelay: `${d}ms` }} />
                  ))}
                </div>
              </div>
            </div>
          )}
          {moduleId === "offer" && !testimonialSubmitted && (() => {
            const completed = detectCompletedPrompts("offer", messages);
            const prompts = MODULE_PROMPTS["offer"] || [];
            return prompts.length > 0 && completed.size === prompts.length;
          })() && (
            <ReviewWidget userId={userId} onSubmitted={onTestimonialSubmitted} trigger="offer_review" />
          )}
          {(() => {
            const transition = MODULE_TRANSITIONS[moduleId];
            if (!transition) return null;
            // See `awaitingHerWord`: no exits while a lock or a revise is open.
            if (awaitingHerWord) return null;
            const visiblePrompts = getVisiblePrompts(moduleId, messages);
            if (visiblePrompts.length === 0) return null;
            /* DONE IS COUNTED, NOT CLAIMED (2026-08-30).
             *
             * This read `detectCompletedPrompts`, which scans the transcript for
             * `✓ LOCKED:` lines — and a transcript cannot be un-said. So one
             * marker Brill wrote for a section that never reached the database
             * made the whole module read finished, permanently. A live client
             * had four of My Mission's five sections stored, was working through
             * the fifth, and this card sat under her the entire time announcing
             * "Your mission is locked — all confirmed" and offering My Client.
             *
             * The card itself is not the problem and it is NOT being hidden more
             * aggressively: once the module really is complete it stays put, and
             * it has to, because the sidebar is the chrome she will not use. The
             * only thing that changes is what "complete" counts.
             *
             * Same reader the next-section button below has used since 09ca453 —
             * storage, with the transcript kept as the fallback until her
             * outputs land, or the card would blink out on every load. Two
             * affordances answering "where is she up to" now agree, which is the
             * whole lesson of this seam.
             *
             * Note this also subsumes the isStale check below: lockedSections
             * counts LOCKED only, never PAST. It is left in place deliberately —
             * removing a guard while changing the thing it guards is how this
             * area has broken before. */
            const completed = !outputsLoaded
              ? detectCompletedPrompts(moduleId, messages)
              : lockedSections({
                prompts: visiblePrompts,
                savedOutputs,
                stamps: outputStamps?.[moduleId],
                activeNicheId: activeNiche?.id || null,
              });
            if (completed.size < visiblePrompts.length) return null;
            /* Completion is read out of the transcript, and the transcript keeps
               the locks she made under a niche she has since left — so a pivoted
               module reads as finished when it is the opposite of finished. The
               card then sits pinned to the bottom of the module announcing "your
               mission is locked, now we go deep on the person you're building
               for", including directly underneath Brill asking whether to lock
               something. It reads as Brill contradicting itself mid-sentence.
               Nothing is complete while it is still built for the old client. */
            if (visiblePrompts.some((p) => isStale(moduleId, p))) return null;
            /* A rebuild that just finished makes its own offer, by name and with a
               way to decline. Both cards point at the same next module, so showing
               them together asks her the same question twice in two voices — and
               only the rebuild's offer takes her through triage first, which is
               the one that matters mid-pivot. The transition card comes back the
               moment she answers either way. */
            if (rebuildNext) return null;
            const handleCtaClick = transition.nextId
              ? () => handleTransitionTo(transition.nextId)
              : transition.ideationBlueprint
                ? () => onViewIdeationBlueprint?.()
                : () => onViewMasterBlueprint?.();
            return (
              <div style={{ display: "flex", gap: 10, marginBottom: 12, animation: "fadeUp 0.5s ease" }}>
                <div style={{ ...BOT_AVATAR, width: 32, height: 32 }}>
                  <span style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 700 }}>B</span>
                </div>
                <div style={{ background: "var(--surface-grey)", padding: "16px 18px", borderRadius: "16px 16px 16px 4px", boxShadow: "var(--shadow-bubble)", maxWidth: isMobile ? "84%" : BUBBLE_MAX }}>
                  <p style={{ margin: "0 0 14px", fontSize: 15, color: "var(--text-primary)", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                    {transition.message}
                  </p>
                  <button
                    onClick={handleCtaClick}
                    style={{ ...START_BTN, padding: "10px 18px", display: "flex", alignItems: "center", gap: 6 }}
                  >
                    {transition.cta}
                  </button>
                </div>
              </div>
            );
          })()}
          {isAskBrill && chatTab === "review" && reviewPending && activeMessages.length > 0 && (
            <div style={{ alignSelf: "center", width: "100%", maxWidth: 360, background: "var(--bg-surface)", border: "1px solid var(--accent-line)", borderRadius: 12, padding: "16px 18px", boxShadow: "var(--shadow-soft)", animation: "fadeUp 0.4s ease" }}>
              <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", textAlign: "center" }}>How many stars would you give it?</p>
              <div style={{ display: "flex", gap: 4, justifyContent: "center", marginBottom: 12 }}>
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button" aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    onClick={() => setReviewPendingStars(n)}
                    onMouseEnter={() => setReviewQuickHover(n)}
                    onMouseLeave={() => setReviewQuickHover(0)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 26, padding: 2, opacity: (reviewQuickHover || reviewPendingStars) >= n ? 1 : 0.25, transition: "opacity 0.1s" }}
                  >⭐</button>
                ))}
              </div>
              <button type="button"
                onClick={submitPendingReview}
                disabled={!reviewPendingStars || reviewQuickSubmitting}
                style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: reviewPendingStars ? "var(--btn-bg)" : "var(--btn-off-bg)", color: reviewPendingStars ? "var(--btn-text)" : "var(--btn-off-text)", fontSize: 13, fontWeight: 600, cursor: reviewPendingStars ? "pointer" : "not-allowed" }}
              >
                {reviewQuickSubmitting ? "Saving…" : "Save my testimonial"}
              </button>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        </div>

        {!askHome && (
        <div style={{
          background: "var(--bg-surface)", borderTop: "1px solid var(--border-subtle)",
          padding: isMobile ? "10px" : `12px ${COLUMN_GUTTER}`, flexShrink: 0,
          paddingBottom: isMobile ? "10px" : "12px",
        }}>
          {askChat?.problem && isAskBrill && chatTab === "general" && (
            <div style={{ ...NOTICE, padding: "7px 10px", marginBottom: 8 }}>
              <AlertTriangle size={13} color="var(--warn-line)" style={{ flexShrink: 0 }} />
              {askChat.problem}
            </div>
          )}
          {saveWarning && (
            <div style={{ ...NOTICE, padding: "7px 10px", marginBottom: 8 }}>
              <AlertTriangle size={13} color="var(--warn-line)" style={{ flexShrink: 0 }} />
              Message may not have saved — check your connection and try again.
            </div>
          )}
          {/* ── A lock that didn't reach the database ──────────────────────
              Louder than the message warning above it, and deliberately so: a
              message that fails to save is a line of chat, and this is a whole
              section of her work. It names the section, because "something
              didn't save" sends her hunting.

              The tick has already been taken back off by the time this renders
              (see revertLock in App.jsx) — so the sidebar and this strip agree,
              rather than one claiming saved while the other says otherwise. */}
          {lockFailure && (() => { const lockAdvice = lockFailureAdvice(lockFailure.reason); return (
            <div style={{ ...NOTICE, padding: "9px 11px", marginBottom: 8, flexWrap: "wrap" }}>
              <AlertTriangle size={13} color="var(--warn-line)" style={{ flexShrink: 0 }} />
              {/* NAME THE CAUSE, NOT JUST THE FAILURE. "It didn't save" was true
                  for every one of these and the next action is different for each,
                  so the real answer lived in a console she was never going to
                  open. A tab left open across a schema deploy kept sending the old
                  write and failed on EVERY lock; the fix was to refresh, and
                  nothing on screen said so. See lockFailureReason in
                  utils/messages.js — each branch keys off a code the database
                  actually returned, so this never guesses. */}
              <span style={{ flex: 1, minWidth: 180 }}>
                <b style={{ fontWeight: 700 }}>{lockFailure.sectionName}</b> didn’t save.
                It’s still here in the chat, so nothing is lost — but it isn’t locked yet.
                {lockAdvice.text && <> {lockAdvice.text}</>}
              </span>
              {/* "Try again" is hidden when it cannot possibly work. Offering a
                  button that is guaranteed to fail is how she spends a morning
                  pressing it. */}
              {lockAdvice.canRetry && (
              <button
                onClick={onRetryLock}
                style={{
                  padding: "4px 11px", borderRadius: 6, border: "1px solid var(--warn-line)",
                  background: "transparent", color: "var(--warn-text)", fontSize: 11.5,
                  fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
                }}
              >
                Try again
              </button>
              )}
              <button
                onClick={onDismissLockFailure}
                aria-label="Dismiss"
                style={{
                  padding: "4px 8px", borderRadius: 6, border: "none", background: "transparent",
                  color: "var(--warn-text)", fontSize: 11.5, cursor: "pointer",
                  fontFamily: "inherit", flexShrink: 0,
                }}
              >
                Dismiss
              </button>
            </div>
          ); })()}
          {renderComposer(false)}
        </div>
        )}
      </div>
      {rightPanel}
      {transitioning && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "linear-gradient(135deg, var(--brand-teal-dark), var(--brand-teal))", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", animation: "fadeOverlay 0.35s ease forwards" }}>
          <div style={{ color: "#FFFFFF", fontSize: 24, fontWeight: 700, marginBottom: 10, letterSpacing: "-0.3px" }}>{MODULE_META[transitioning]?.label || transitioning}</div>
          <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>Loading your next module →</div>
        </div>
      )}
    </div>
  );
}
